import { useState } from 'react';
import PropTypes from 'prop-types';
import './Filters.css';
import { ChevronDown, CloseIcon, Columns, Filter } from '@/ui/icons';

// "Filtros" UX (the prototype's single-button filter panel + applied
// chips). One button with an active-count badge opens a panel of grouped
// multi-select checkboxes; applied values surface as removable chips below the
// toolbar. Styles: atoms.css (.fbtn--filters / .fpanel / .fchip).
//
// groups: [{ key, label, value: string[], options: [{value,label,n?}], onChange }]

const countActive = (groups) =>
  groups.reduce((n, g) => n + (g.value?.length || 0), 0);

const groupShape = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.array,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
  }),
);

export function FilterButton({ groups }) {
  const [open, setOpen] = useState(false);
  const count = countActive(groups);
  const clearAll = () => groups.forEach((g) => g.onChange([]));
  return (
    <div className="fdrop">
      <button
        type="button"
        className={`fbtn fbtn--filters${count ? ' fbtn--on' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Filter size={15} />
        <span className="fbtn__v">Filtros</span>
        {count > 0 && <span className="fbtn__badge">{count}</span>}
        <ChevronDown size={13} />
      </button>
      {open && (
        <>
          <div className="fdrop__scrim" onClick={() => setOpen(false)} />
          <div className="fpanel" role="dialog" aria-label="Filtros">
            <div className="fpanel__head">
              <span className="fpanel__title">Filtros</span>
              {count > 0 && (
                <button
                  type="button"
                  className="fpanel__clear"
                  onClick={clearAll}
                >
                  Limpiar todo
                </button>
              )}
            </div>
            <div className="fpanel__body">
              {groups.map((g) => {
                const sel = g.value || [];
                return (
                  <div className="fgroup" key={g.key}>
                    <div className="fgroup__label">
                      {g.label}
                      {sel.length > 0 && (
                        <span className="fgroup__n">{sel.length}</span>
                      )}
                    </div>
                    <div className="fgroup__opts">
                      {g.options.map((o) => {
                        const on = sel.includes(o.value);
                        return (
                          <button
                            key={o.value}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={on}
                            className={`fopt${on ? ' is-on' : ''}`}
                            onClick={() =>
                              g.onChange(
                                on
                                  ? sel.filter((x) => x !== o.value)
                                  : sel.concat(o.value),
                              )
                            }
                          >
                            <span className="fopt__box" />
                            <span className="fopt__lbl">{o.label}</span>
                            {o.n != null && (
                              <span className="fopt__n">{o.n}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

FilterButton.propTypes = { groups: groupShape.isRequired };

// column-toggle (the prototype's ColumnsButton). Lives IN the table
// toolbar (not in settings). Same panel chrome as FilterButton, but a flat list
// of show/hide checkboxes; `lockedNote` names the columns that are always shown.
//   opts:  [[key, label], …]  — the toggleable columns
//   value: string[]           — currently visible keys
export function ColumnsButton({ opts, value, onChange, lockedNote }) {
  const [open, setOpen] = useState(false);
  const toggle = (k) =>
    onChange(
      value.includes(k) ? value.filter((x) => x !== k) : value.concat(k),
    );
  return (
    <div className="fdrop">
      <button
        type="button"
        className="fbtn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Columns size={15} />
        <span className="fbtn__v">Columnas</span>
        <ChevronDown size={13} />
      </button>
      {open && (
        <>
          <div className="fdrop__scrim" onClick={() => setOpen(false)} />
          <div className="fpanel" role="dialog" aria-label="Columnas">
            <div className="fpanel__head">
              <span className="fpanel__title">Columnas</span>
            </div>
            <div className="fpanel__body">
              <div className="fgroup">
                <div className="fgroup__opts">
                  {opts.map(([k, l]) => {
                    const on = value.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={on}
                        className={`fopt${on ? ' is-on' : ''}`}
                        onClick={() => toggle(k)}
                      >
                        <span className="fopt__box" />
                        <span className="fopt__lbl">{l}</span>
                      </button>
                    );
                  })}
                </div>
                {lockedNote && <div className="fgroup__note">{lockedNote}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

ColumnsButton.propTypes = {
  opts: PropTypes.arrayOf(PropTypes.array).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  lockedNote: PropTypes.string,
};

export function FilterChips({ groups }) {
  const chips = [];
  groups.forEach((g) =>
    (g.value || []).forEach((v) => {
      const o = g.options.find((x) => x.value === v);
      chips.push({
        id: `${g.key}:${v}`,
        glabel: g.label,
        label: o ? o.label : v,
        remove: () => g.onChange((g.value || []).filter((x) => x !== v)),
      });
    }),
  );
  if (chips.length === 0) return null;
  const clearAll = () => groups.forEach((g) => g.onChange([]));
  return (
    <div className="fchips">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          className="fchip"
          onClick={c.remove}
          title={`Quitar ${c.glabel}: ${c.label}`}
        >
          <span className="fchip__g">{c.glabel}</span>
          <span className="fchip__v">{c.label}</span>
          <CloseIcon size={12} />
        </button>
      ))}
      {chips.length > 1 && (
        <button type="button" className="fchip fchip--clear" onClick={clearAll}>
          Limpiar todo
        </button>
      )}
    </div>
  );
}

FilterChips.propTypes = { groups: groupShape.isRequired };
